import { useState, useCallback, useRef } from 'react';
import { useQuery } from '@apollo/client/react';
import { useOrganizationStore } from '../../../stores/useOrganizationStore';
import { GET_CONTACT_VALUATIONS } from '../../../lib/graphql/queries/contacts';
import type { GQLValuationItem } from './useContact';

const PAGE_SIZE = 50;

interface GetContactValuationsData {
  contact: {
    contactId: string;
    valuations: {
      items: GQLValuationItem[];
      total: number;
      hasMore: boolean;
    };
  } | null;
}

export const useContactValuationsGQL = (contactId: string) => {
  const { organization } = useOrganizationStore();
  const [currentPage, setCurrentPage] = useState(0);

  const { data, loading, error } = useQuery<GetContactValuationsData>(GET_CONTACT_VALUATIONS, {
    variables: {
      organizationId: organization?.id ?? '',
      contactId,
      limit: PAGE_SIZE,
      offset: currentPage * PAGE_SIZE,
      sortOrder: 'DESC',
    },
    skip: !organization?.id || !contactId,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const items = data?.contact?.valuations?.items ?? [];
  const queryTotal = data?.contact?.valuations?.total;
  const hasMore = data?.contact?.valuations?.hasMore ?? false;

  // Preserve only the last completed total for this exact tenant/contact while
  // Apollo changes pagination variables. A different scope gets no fallback.
  const totalScope = `${organization?.id ?? ''}:${contactId}`;
  const stableTotal = useRef<{ scope: string; value: number } | null>(null);
  if (!loading && queryTotal !== undefined) {
    stableTotal.current = { scope: totalScope, value: queryTotal };
  }
  const total = stableTotal.current?.scope === totalScope ? stableTotal.current.value : 0;

  const handleNextPage = useCallback(() => setCurrentPage((p) => p + 1), []);

  return { items, total, hasMore, loading, error, hasNext: hasMore, handleNextPage };
};
